import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IL_COLORS } from "../../../../constants/investmentLeague";
import {
  mapRecipientLeaderboardResponse,
  computeFilteredSummary,
} from "../../utils/mapRecipientLeaderboardData";
import SummaryCards from "./SummaryCards";
import RecipientCharts from "./RecipientCharts";
import RecipientTable from "./RecipientTable";
import RecipientDetailDrawer from "./RecipientDetailDrawer";
import TabLoader from "../TabLoader";

const InfoCircleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#38BDF8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const RecipientLeaderboard = ({ data, loading, error, onRetry, isAdmin = false }) => {
  const theme = useTheme();
  const t = theme.custom;
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg"));

  // Header Filters
  const [selectedQuarter, setSelectedQuarter] = useState("All Quarters");
  const [unitType, setUnitType] = useState("All Units");

  // Selected Unit for Detail Drawer (Admins only)
  const [selectedUnit, setSelectedUnit] = useState(null);

  // Measure table container height so detail panel matches it exactly
  const tableContainerRef = React.useRef(null);
  const [tableHeight, setTableHeight] = useState(null);

  // Map API response to canonical 11 units
  const mappedData = useMemo(() => {
    return mapRecipientLeaderboardResponse(data);
  }, [data]);

  const units = mappedData.units;

  // Selected unit synchronized with data (accessible to admins only)
  const activeSelectedUnit = useMemo(() => {
    if (!isAdmin || !selectedUnit) return null;
    return units.find((u) => u.unitName === selectedUnit.unitName) || selectedUnit;
  }, [isAdmin, selectedUnit, units]);

  React.useLayoutEffect(() => {
    if (!tableContainerRef.current) return;

    const updateHeight = () => {
      if (tableContainerRef.current) {
        setTableHeight(tableContainerRef.current.offsetHeight);
      }
    };

    updateHeight();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateHeight();
      });
      resizeObserver.observe(tableContainerRef.current);
    }

    window.addEventListener("resize", updateHeight);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [units, selectedQuarter, unitType, activeSelectedUnit]);

  // Compute summary KPI cards reflecting the active filters
  const summary = useMemo(() => {
    return computeFilteredSummary(
      units,
      selectedQuarter,
      unitType,
      mappedData.rawSummary
    );
  }, [units, selectedQuarter, unitType, mappedData.rawSummary]);

  if (loading && (!data || units.length === 0)) {
    return <TabLoader message="Loading investment recipient leaderboard..." />;
  }

  if (error && (!data || units.length === 0)) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: "14px", color: t.text2, mb: 2 }}>{error}</Typography>
        {onRetry && (
          <Typography
            component="button"
            onClick={onRetry}
            sx={{
              fontSize: "13px",
              color: IL_COLORS.positive,
              cursor: "pointer",
              background: "none",
              border: "none",
              fontWeight: 600,
            }}
          >
            Try again
          </Typography>
        )}
      </Box>
    );
  }

  if (!data || units.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: "14px", color: t.text3 }}>
          No recipient leaderboard data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "1440px", mx: "auto", pb: 4 }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: "22px", md: "26px" },
              fontWeight: 700,
              color: t.text,
              mb: 0.5,
            }}
          >
            Investment Recipient Leaderboard
          </Typography>
          <Typography sx={{ fontSize: "13px", color: t.text3 }}>
            Which MAC and Group units received the most investments and what return did they generate in each quarter?
          </Typography>
        </Box>

        {/* Filters on top right */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          {/* Quarter */}
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 110 },
              "& .MuiInputBase-root": { height: "auto" },
            }}
          >
            <Select
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: "16px",
                fontSize:     "11px",
                fontWeight:   600,
                background:   t.surface2,
                height:       "auto",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: t.border2,
                },
                "& .MuiSelect-select": {
                  py:        0.5,
                  px:        1.5,
                  minHeight: "unset !important",
                  lineHeight: 1.4,
                },
              }}
            >
              <MenuItem value="All Quarters">All Quarters</MenuItem>
              <MenuItem value="Q1">Q1</MenuItem>
              <MenuItem value="Q2">Q2</MenuItem>
              <MenuItem value="Q3">Q3</MenuItem>
              <MenuItem value="Q4">Q4</MenuItem>
            </Select>
          </FormControl>

          {/* Unit Type */}
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 150 },
              "& .MuiInputBase-root": { height: "auto" },
            }}
          >
            <Select
              value={unitType}
              onChange={(e) => setUnitType(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: "16px",
                fontSize:     "11px",
                fontWeight:   600,
                background:   t.surface2,
                height:       "auto",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: t.border2,
                },
                "& .MuiSelect-select": {
                  py:        0.5,
                  px:        1.5,
                  minHeight: "unset !important",
                  lineHeight: 1.4,
                },
              }}
            >
              <MenuItem value="All Units">All (MACs & Groups)</MenuItem>
              <MenuItem value="MAC">MAC</MenuItem>
              <MenuItem value="Groups">Groups</MenuItem>
            </Select>
          </FormControl>

          {/* Financial Year - At the last */}
          <Box
            sx={{
              px:           1.5,
              py:           0.5,
              borderRadius: "16px",
              background:   "rgba(45,138,91,0.12)",
              border:       `1px solid rgba(45,138,91,0.25)`,
              fontSize:     "11px",
              fontWeight:   600,
              color:        IL_COLORS.positive,
              whiteSpace:   "nowrap",
              lineHeight:   1.4,
              display:      "inline-flex",
              alignItems:   "center",
              justifyContent: "center",
            }}
          >
            FY-2026-27
          </Box>
        </Box>
      </Box>

      {/* Summary KPI Cards */}
      <SummaryCards summary={summary} unitType={unitType} />

      {/* Charts Row */}
      <RecipientCharts
        units={units}
        selectedQuarter={selectedQuarter}
        unitType={unitType}
      />

      {/* Main Table + Detail Panel Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isAdmin && activeSelectedUnit && !isMobileOrTablet
            ? { lg: "1fr 380px", xl: "1fr 420px" }
            : "1fr",
          gap: 2,
          mb: 2.5,
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }} ref={tableContainerRef}>
          <RecipientTable
            units={units}
            selectedQuarter={selectedQuarter}
            unitType={unitType}
            onUnitTypeChange={setUnitType}
            selectedUnit={isAdmin ? activeSelectedUnit : null}
            onSelectUnit={isAdmin ? setSelectedUnit : undefined}
            isAdmin={isAdmin}
          />
        </Box>

        {/* Inline Detail Panel on Desktop (Admins only) */}
        {isAdmin && activeSelectedUnit && !isMobileOrTablet && (
          <Box
            sx={{
              height: tableHeight ? `${tableHeight}px` : "auto",
              maxHeight: tableHeight ? `${tableHeight}px` : "100%",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <RecipientDetailDrawer
              unit={activeSelectedUnit}
              onClose={() => setSelectedUnit(null)}
            />
          </Box>
        )}
      </Box>

      {/* Mobile/Tablet Slide-over Drawer (Admins only) */}
      {isAdmin && isMobileOrTablet && (
        <Drawer
          anchor="right"
          open={Boolean(activeSelectedUnit)}
          onClose={() => setSelectedUnit(null)}
          PaperProps={{
            sx: {
              width: { xs: "100%", sm: 440 },
              background: t.surface,
              borderLeft: `1px solid ${t.border}`,
              p: 0,
            },
          }}
        >
          {activeSelectedUnit && (
            <Box sx={{ height: "100%", p: 2 }}>
              <RecipientDetailDrawer
                unit={activeSelectedUnit}
                onClose={() => setSelectedUnit(null)}
              />
            </Box>
          )}
        </Drawer>
      )}

      {/* Information Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: "10px",
          maxWidth: isAdmin && activeSelectedUnit && !isMobileOrTablet
            ? { lg: "calc(100% - 396px)", xl: "calc(100% - 436px)" }
            : "100%",
        }}
      >
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          <InfoCircleIcon />
        </Box>
        <Box>
          <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: t.text }}>
            Showing 11 units (8 MACs + 3 Groups)
          </Typography>
          <Typography sx={{ fontSize: "11px", color: t.text3, mt: 0.25 }}>
            ROI is calculated as: (Return Generated / Investment Received) × 100 using the recipient unit's variance for each quarter.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RecipientLeaderboard;

